import { escapeQueryStringValue } from '@src/util/elastic/escapeQueryStringValue';

/**
 * Builds an Elasticsearch prefix query from the configured search key(s).
 *
 * Mirrors the behaviour of the legacy `DebounceCloudFetchSelect`:
 * - A single key produces `key:term*`.
 * - Multiple keys are OR-joined: `firstName:Rica* OR lastName:Rica*`.
 * - `email` targets `email.keyword` so complete addresses are not tokenized.
 * - `idCard` strips dots and searches the normalized identifier as a partial match.
 *
 * The typed term is escaped before the trailing `*` is appended, so reserved
 * characters (`:`, `(`, `-`, ...) are matched literally instead of being parsed
 * as query_string syntax and rejected by Elasticsearch.
 *
 * @returns the query string, or an empty string when the term is blank.
 */
export const buildSearchQuery = (searchKey: string | string[], searchTerm: string): string => {
  const trimmed = searchTerm.trim();
  if (!trimmed) return '';

  const toClause = (key: string): string => {
    if (key === 'email') {
      return `email.keyword:*${escapeQueryStringValue(trimmed)}*`;
    }
    if (key === 'idCard') {
      return `${key}:*${escapeQueryStringValue(trimmed.replace(/\./g, ''))}*`;
    }
    return `${key}:${escapeQueryStringValue(trimmed)}*`;
  };

  if (Array.isArray(searchKey)) {
    return searchKey.map(toClause).join(' OR ');
  }

  return toClause(searchKey);
};
